import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Check } from "lucide-react";
import { trackCheckoutCompleted } from '@/lib/analytics';
import { getPlanFromPriceId } from '@/lib/stripe-config';
import { loadSubscriptionPrices } from '@/lib/subscription-prices';

export default function SubscriptionSuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // Track checkout completion
    // Use checkout ID to allow multiple checkouts of the same plan in same session
    const priceId = searchParams.get('price_id') || sessionStorage.getItem('last_subscription_price_id');
    const checkoutId = sessionStorage.getItem('last_checkout_id');
    
    let active = true;
    if (priceId && checkoutId) {
      // VOY-601: plan lookup needs the subscription_prices rows. A failed load skips tracking.
      loadSubscriptionPrices().then((prices) => {
        if (!active) return;
        const planInfo = getPlanFromPriceId(priceId, prices);
        if (planInfo) {
          // Use checkout ID-specific tracking key to allow multiple checkouts
          // This allows the same plan to be purchased multiple times in the same session
          const trackingKey = `checkout_completed_${checkoutId}`;
          const alreadyTracked = sessionStorage.getItem(trackingKey);

          if (!alreadyTracked) {
            trackCheckoutCompleted(planInfo.plan, planInfo.isAnnual);
            // Mark this specific checkout as tracked to prevent duplicate events for same checkout
            // (e.g., on page refresh)
            sessionStorage.setItem(trackingKey, 'true');
          }

          // Clear checkout info from sessionStorage after processing
          sessionStorage.removeItem('last_subscription_price_id');
          sessionStorage.removeItem('last_checkout_id');
        }
      }, () => {
        // Already logged by loadSubscriptionPrices; tracking is skipped.
      });
    }

    // Automatically redirect after 5 seconds
    const timeout = setTimeout(() => {
      navigate('/profile');
    }, 5000);

    return () => {
      active = false;
      clearTimeout(timeout);
    };
  }, [navigate, searchParams]);

  return (
    <div className="min-h-screen bg-transparent">
      <div className="container mx-auto py-12 px-4">
        <div className="max-w-md mx-auto">
          <Card className="bg-white/90 backdrop-blur-lg border border-white/30 shadow-2xl rounded-2xl">
            <CardHeader>
              <div className="flex items-center justify-center mb-4">
                <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center shadow-lg">
                  <Check className="h-8 w-8 text-green-600" />
                </div>
              </div>
              <CardTitle className="text-center text-2xl font-bold text-gray-900">
                Payment Successful!
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <p className="text-center text-gray-700 text-base">
                Thank you for your purchase. Your subscription is now active.
              </p>
              <div className="flex flex-col gap-3 pt-2">
                <Button 
                  onClick={() => navigate('/profile')}
                  className="w-full rounded-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-semibold"
                >
                  Go to Profile
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => navigate('/')}
                  className="w-full rounded-full border-gray-300 hover:bg-gray-50"
                >
                  Return to Home
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
